import { ERROR_CODE, UniversalError } from "@packages/core";
import { type StandardSchemaV1Issue } from "@tanstack/react-form";
import { t as translate } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/** 翻訳関数を取得する。 */
export function useTranslate() {
  return useTranslation(undefined, { useSuspense: false })[0];
}

/**
 * エラーメッセージを取得する。
 * Reactコンポーネント内で使用する場合は、
 * 代わりに`useGetErrorMessage`フックを使用する。
 * `UniversalError`インスタンスまたはエラーコードを受け取り、
 * 翻訳されたエラーメッセージを返す。
 * @param error - エラーコードまたは`UniversalError`インスタンス
 * @returns 翻訳されたエラーメッセージ
 */
export function getErrorMessage(error?: string | UniversalError) {
  if (error) {
    const errorCode = error instanceof UniversalError ? error.code : error;
    return translate("%Error." + errorCode + ".0%");
  } else {
    return undefined;
  }
}

export function useGetErrorMessage() {
  const translate = useTranslate();

  return useCallback(
    function (error?: string | UniversalError | StandardSchemaV1Issue) {
      if (error instanceof UniversalError) {
        return translate("%Error." + error.code + ".0%", {
          $: error.data
            ? error.data.private === undefined
              ? error.data.public
              : error.data.public === undefined
                ? error.data.private
                : Object.assign(Object.create(null), error.data.private, error.data.public)
            : undefined,
        });
      } else if (error instanceof Error) {
        return error.message;
      } else if (typeof error === "string") {
        return translate("%Error." + error + ".0%");
      } else if (typeof error === "object" && error !== null) {
        if ("message" in error && typeof error.message === "string") {
          return translate("%Error." + error.message + ".0%", { $: error });
        } else {
          console.error("不明なエラー形式のエラーメッセージの取得を試みました。", error);
          return translate("%Error." + ERROR_CODE.UNKNOWN + ".0%", { $: error });
        }
      } else {
        return undefined;
      }
    },
    [translate],
  );
}
